import { SourceType, isDefined, schSourceType, syntaxFNMA } from '@aloft/toolkit-shared';
import { Icon, Input, Tooltip } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Popover, PopoverContent, PopoverTrigger } from '@aloft/toolkit-sui/chakra';

import { useRef } from 'react';
import { Controller, ControllerRenderProps } from 'react-hook-form';

import { getDataSourceDisplayInfo, getSelectSourceTooltip } from '../utils.ts';
import { EditableFieldTrigger } from './shared/EditableFieldTrigger.tsx';
import { PopoverInputHeader } from './shared/PopoverInputHeader.tsx';
import { PopoverOptionsList } from './shared/PopoverOptionsList.tsx';
import { useEditableField } from './shared/useEditableField.tsx';
import { usePropertyGridCellFocus } from './shared/usePropertyGridCellFocus.ts';
import { popoverInputSx } from './styles.ts';
import { PropertyGridFieldProps } from './types.ts';

export const EditableDateField = ({
  columnIndex,
  field,
  isArv = false,
  colSpan = 1,
  control,
  getValues,
  setValue,
  required = false,
}: PropertyGridFieldProps) => {
  const { toDisplayValue } = field;

  const {
    propertyId,
    propertyDataFieldName,
    value,
    displayValue,
    conflictedFields,
    selectedSourceValue,
    showConflictButton,
    setShowConflictButton,
    inputFocused,
    setInputFocused,
    customInput,
    options,
    fieldHasConflict,
    makeHandleOnClose,
    resolveSource,
    commitSource,
  } = useEditableField({ columnIndex, field, isArv, control, getValues, setValue, required });

  const fieldRef = useRef<HTMLButtonElement>(null);
  const {
    isAlertHighlighted,
    isAlertStyled,
    alertStyleVariant,
    alertCellTooltip,
    clearAlertTarget,
  } = usePropertyGridCellFocus({
    cellKeyId: field.fieldName,
    propertyId,
    fieldRef,
  });

  const handleSetFieldValue = (
    onChange: ControllerRenderProps['onChange'],
    newValue?: string,
    newSource?: SourceType,
  ) => {
    if (newSource === schSourceType.enum.unknown) {
      if (required) return;
      onChange(undefined);
      commitSource(schSourceType.enum.unknown);
      return;
    }
    let parsedNewValue: string | undefined;
    if (newValue && newValue.trim() !== '') {
      try {
        const isoMonth = syntaxFNMA.schIsoMonth.parse(newValue.trim());
        parsedNewValue = isoMonth;
      } catch {
        console.warn('Invalid date format:', newValue);
        return;
      }
    }
    onChange(parsedNewValue);

    const displayParsedValue = toDisplayValue
      ? toDisplayValue(parsedNewValue, propertyId)
      : parsedNewValue;

    const resolvedSource = resolveSource(parsedNewValue, newSource, displayParsedValue);
    commitSource(resolvedSource);
    clearAlertTarget();
  };

  return (
    <Controller
      name={propertyDataFieldName}
      control={control}
      render={({ field: { onChange, ref }, fieldState: { error } }) => {
        const monthValue = value ?? '';

        const selectedSource = selectedSourceValue ?? (isDefined(value) ? 'custom' : 'none');

        const showIcon = isDefined(error) || selectedSource !== 'none';
        const icon = error
          ? 'error-warning'
          : getDataSourceDisplayInfo(selectedSource ?? 'none').icon;
        const iconColor = error ? 'alertError' : undefined;
        const iconTooltipLabel = error ? error.message : getSelectSourceTooltip(selectedSource);

        return (
          <Popover gutter={-32} initialFocusRef={customInput} variant="inline" isLazy matchWidth>
            {({ onClose }) => {
              const handleOnClose = makeHandleOnClose(onClose);

              return (
                <>
                  <PopoverTrigger>
                    <EditableFieldTrigger
                      ref={fieldRef}
                      colSpan={colSpan}
                      displayValue={displayValue}
                      fieldHasConflict={fieldHasConflict}
                      showConflictButton={showConflictButton}
                      onMouseEnter={() => setShowConflictButton(true)}
                      onMouseLeave={() => setShowConflictButton(false)}
                      onClick={handleOnClose}
                      propertyDataFieldName={propertyDataFieldName}
                      conflictedFields={conflictedFields}
                      showIcon={showIcon}
                      icon={icon}
                      iconTooltipLabel={iconTooltipLabel ?? ''}
                      required={required}
                      isAlertStyled={isAlertStyled}
                      alertStyleVariant={alertStyleVariant}
                      isAlertFocusStyled={isAlertHighlighted}
                      alertTitle={alertCellTooltip}
                    />
                  </PopoverTrigger>
                  <PopoverContent borderRadius="0" width="100%">
                    <PopoverInputHeader inputFocused={inputFocused}>
                      <Input
                        type="month"
                        ref={(instance) => {
                          ref(instance);
                          customInput.current = instance;
                        }}
                        value={monthValue}
                        __dangerDoNotUseSx={popoverInputSx}
                        onFocus={() => setInputFocused(true)}
                        onBlur={() => {
                          setInputFocused(false);
                          handleOnClose();
                        }}
                        onChange={(inputValue) => {
                          handleSetFieldValue(onChange, inputValue);
                        }}
                        onClear={() => {
                          handleSetFieldValue(onChange, undefined);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            handleOnClose();
                          } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                            handleSetFieldValue(onChange, monthValue);
                            handleOnClose();
                          }
                        }}
                      />
                      <Tooltip label={iconTooltipLabel}>
                        <Icon icon={icon} color={iconColor} />
                      </Tooltip>
                    </PopoverInputHeader>
                    <PopoverOptionsList
                      options={options}
                      onSelect={({ value: newValue, source: newSource }) => {
                        handleSetFieldValue(onChange, newValue, newSource);
                        handleOnClose();
                      }}
                    />
                  </PopoverContent>
                </>
              );
            }}
          </Popover>
        );
      }}
    />
  );
};
